<script setup lang="ts">
import { faqService } from '@/api/services/faq-service'
import BaseLayout from '@/components/base/BaseLayout.vue'
import type { ApiCursorBody } from '@/types/api-response.types'
import type { Faq } from '@/types/faq.types'
import { onMounted, onUnmounted, ref } from 'vue'
import FaqListItem from '../components/FaqListItem.vue'

const faqList = ref<Faq[]>([])
const faqCursorInfo = ref<ApiCursorBody<Faq>>()
const isLoading = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

const expandedItems = ref<number[]>([])

const toggleItem = (id: number) => {
  if (expandedItems.value.includes(id)) {
    expandedItems.value = expandedItems.value.filter((itemId) => itemId !== id)
  } else {
    expandedItems.value.push(id)
  }
}

const isExpanded = (id: number): boolean => {
  return expandedItems.value.includes(id)
}

const loadMoreFaqs = async () => {
  if (isLoading.value || !faqCursorInfo.value?.hasNext) return

  isLoading.value = true
  try {
    const nextCursor = faqCursorInfo.value?.nextCursor
    const faqCursor = await faqService.getFaqs(nextCursor, 20, { ascending: false })
    faqList.value = [...faqList.value, ...faqCursor.data]
    faqCursorInfo.value = faqCursor
  } catch (error) {
    console.error('FAQ 로드 중 오류가 발생했습니다:', error)
  } finally {
    isLoading.value = false
  }
}

const setupIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && faqCursorInfo.value?.hasNext && !isLoading.value) {
        loadMoreFaqs()
      }
    },
    { threshold: 0.1 },
  )

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value)
  }
}

onMounted(async () => {
  const faqCursor = await faqService.getFaqs(null, 20, { ascending: false })
  faqList.value = faqCursor.data
  faqCursorInfo.value = faqCursor
  setupIntersectionObserver()
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<template>
  <BaseLayout>
    <div class="faq-view">
      <div class="faq-view-header">
        <div class="faq-view-header-title title-md white">FAQ</div>
      </div>
      <div class="faq-list">
        <FaqListItem
          v-for="item in faqList"
          :key="item.idx"
          :item="item"
          :is-expanded="isExpanded(item.idx)"
          @toggle="toggleItem"
        />
        <!-- 로딩 트리거 요소 -->
        <div ref="loadMoreTrigger" class="load-more-trigger">
          <div v-if="isLoading" class="loading-spinner">로딩 중...</div>
        </div>
      </div>
    </div>
  </BaseLayout>
</template>

<style scoped>
.faq-list {
  padding: 0px;
  margin-top: 40px;
}

.load-more-trigger {
  width: 100%;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
}

.loading-spinner {
  color: var(--gray-600);
  font-size: 14px;
}

@media (max-width: 1365px) {
  .faq-view-header {
    padding: 16px 20px;
  }

  .faq-list {
    margin-top: 12px;
    padding: 0px 20px;
  }
}
</style>
